import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import UpdateIcon from '@mui/icons-material/Update'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ReuniaoDialog } from '@/components/agenda/ReuniaoDialog'
import { AderenciaDrawer } from '@/components/pipeline/AderenciaDrawer'
import { MotivoPerdaDialog } from '@/components/pipeline/MotivoPerdaDialog'
import { ClasseScoreChip } from '@/components/pipeline/ClasseScoreChip'
import { tempoRelativo } from '@/components/pipeline/tempoRelativo'
import { ehConflitoVersao, erroMotivoPerdaDe, mensagemErroDe } from '@/components/pipeline/erros'
import { comMovimentacaoAgora } from '@/components/pipeline/movimentacao'
import { oportunidadeService } from '@/services/oportunidadeService'
import { dashboardService } from '@/services/dashboardService'
import { useAuth } from '@/hooks/useAuth'
import { Oportunidade } from '@/types/oportunidade'
import { DashboardResumo } from '@/types/dashboard'
import {
  CLASSES_SCORE,
  CLASSE_SCORE_LABEL,
  ClasseScore,
  DadosPerda,
  ErroMotivoPerda,
  MSG_CONFLITO_VERSAO,
} from '@/types/scoreComercial'
import {
  ETAPA_COMERCIAL_COR,
  ETAPA_COMERCIAL_LABEL,
  EtapaComercial,
  MOTIVO_AGENDAMENTO_INDISPONIVEL,
  StatusReserva,
  permiteAgendamento,
  podeTransicionar,
} from '@/types/statusReserva'
import { QuadroRolavel } from '@/components/pipeline/QuadroRolavel'
import { formatarCnpj } from '@/utils/cnpj'

/**
 * Pipeline Comercial do CONSULTOR (Onda 5 / A10): kanban de 6 etapas (`EtapaComercial`),
 * com Perdido recolhido num painel lateral (o Figma omite a coluna). O back-office segue
 * no kanban de 18 status (`KanbanPage`) — é a leitura em dois níveis da decisão 6.4.
 *
 * Regra que não se discute: a etapa é DERIVADA no backend e nunca gravada. Mover um card
 * aqui é uma AÇÃO SEMÂNTICA — agendar reunião, transicionar status, cancelar com motivo —
 * e a etapa muda por derivação na resposta. Nenhum PATCH de etapa existe nesta tela.
 *
 * Decisão 8.1 (28/09, reverte a 6.3): o card mostra a CLASSE do Score Comercial
 * (`scoreComercial.classe`, derivada da etapa pela tabela versionada do backend — nunca
 * digitada, nunca calculada aqui) e continua mostrando a ADERÊNCIA calculada (0–100,
 * `scoreAderencia`), com rótulo "Aderência" (Q5 assumida). O KPI é "Pipeline total"
 * (soma honesta do backend), nunca "Pipeline ponderado".
 */

/** As cinco colunas do quadro — Perdido fica no painel recolhido ao lado. */
const ETAPAS_VISIVEIS: EtapaComercial[] = [
  'RESERVA',
  'REUNIAO',
  'ESTRUTURACAO',
  'NEGOCIACAO',
  'GANHO',
]

const MOTIVO_BACKOFFICE = 'Movimento feito pela Originei'
const MSG_SEM_USUARIO = 'Sessão sem usuário identificado — entre novamente para mover a reserva'
/** Responsável no Pipeline do consultor: a carteira é a dele (TenantScope), então é sempre ele. */
const RESPONSAVEL_PROPRIO = 'Você'

interface CardPipeline {
  id: string
  numero?: number
  clienteId?: string
  empresa: string
  cnpj?: string | null
  /** Produto (`tipoNecessidade`) — PDF §2.3. */
  produto?: string | null
  valor: number
  proximaAcao?: string | null
  /** Aderência calculada (0–100) — vem pronta na listagem; nada é recalculado aqui. */
  aderencia?: number | null
  /** Classe do Score Comercial — vem pronta na listagem; nunca calculada aqui. */
  classe?: ClasseScore | null
  /** Data da última mudança de etapa (backend). */
  ultimaMovimentacaoEm?: string | null
  /** Versão lida — vai em `versaoEsperada` no PATCH (409 se mudou). */
  versao?: number | null
  etapa: EtapaComercial
  status: StatusReserva
}

/**
 * O que significa soltar `card` na coluna `destino`. Cada resultado vira uma ação
 * semântica — nunca um "gravar etapa".
 */
type Movimento =
  | { tipo: 'nada' }
  | { tipo: 'reuniao' }
  | { tipo: 'transicao'; novoStatus: StatusReserva }
  | { tipo: 'perdido' }
  | { tipo: 'bloqueado'; motivo: string }

const movimentoDe = (card: CardPipeline, destino: EtapaComercial): Movimento => {
  if (card.etapa === destino) return { tipo: 'nada' }
  if (card.etapa === 'PERDIDO') {
    return { tipo: 'bloqueado', motivo: 'Reserva perdida não volta ao funil por aqui — fale com a Originei' }
  }
  if (destino === 'PERDIDO') {
    // Cancelar é do consultor enquanto a máquina de status permitir; depois do contrato
    // assinado o encerramento é decisão da Originei (NAO_CONVERTIDA/ENCERRADA).
    return podeTransicionar(card.status, 'CANCELADA')
      ? { tipo: 'perdido' }
      : { tipo: 'bloqueado', motivo: 'Esta reserva só pode ser encerrada pela Originei' }
  }
  if (card.etapa === 'RESERVA' && destino === 'REUNIAO') {
    // Reunião só depois da aprovação da Originei (06/09) — o backend recusa antes
    // (RESERVA_NAO_APROVADA); aqui o card nem abre o diálogo.
    return permiteAgendamento(card.status)
      ? { tipo: 'reuniao' }
      : { tipo: 'bloqueado', motivo: `Aguardando aprovação da Originei — ${MOTIVO_AGENDAMENTO_INDISPONIVEL.toLowerCase()}` }
  }
  if ((card.etapa === 'RESERVA' || card.etapa === 'REUNIAO') && destino === 'ESTRUTURACAO') {
    // Só APROVADA_ORIGINEI transiciona para EM_ESTRUTURACAO; nos demais status a bola está
    // com a análise da Originei. Vale também a partir de Reserva: desde 16/10/2026 a reunião
    // não é mais obrigatória para entrar em Estruturação (a Originei a solicita quando
    // precisa) — com reunião já ocorrida, o servidor a marca realizada no avanço.
    return card.status === 'APROVADA_ORIGINEI'
      ? { tipo: 'transicao', novoStatus: 'EM_ESTRUTURACAO' }
      : { tipo: 'bloqueado', motivo: `Aguardando análise da Originei — ${MOTIVO_BACKOFFICE.toLowerCase()}` }
  }
  if (card.etapa === 'RESERVA') {
    return { tipo: 'bloqueado', motivo: 'Avance primeiro para Reunião — agende a reunião com o cliente' }
  }
  // Estruturação → Negociação, → Ganho e todo movimento para trás são do back-office.
  return { tipo: 'bloqueado', motivo: MOTIVO_BACKOFFICE }
}

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const formatNumeroReserva = (numero?: number) =>
  numero != null ? `RES-${String(numero).padStart(4, '0')}` : '—'

// Numérico ou alfanumérico (ver `@/utils/cnpj`); sem CNPJ, null para o card omitir a linha.
const formatCNPJ = (cnpj?: string | null): string | null => (cnpj ? formatarCnpj(cnpj) : null)

const MSG_MOVER = 'Não foi possível mover a reserva'

const PipelineConsultorPage: FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()

  // Página do consultor: o back-office acompanha nos 18 status (KanbanPage) — a rota
  // já separa as personas; este guard é a última linha, para acesso direto.
  const ehBackoffice = useMemo(
    () =>
      !!usuario?.permissoes?.some((p) => {
        const up = p.toUpperCase()
        return up === 'ADMIN' || up === 'GERENTE' || up === 'OPERADOR'
      }),
    [usuario],
  )

  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([])
  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  // Drag-and-drop (mesmo padrão HTML5 do KanbanPage — sem lib).
  const [arrastando, setArrastando] = useState<CardPipeline | null>(null)
  const [colunaAlvo, setColunaAlvo] = useState<EtapaComercial | null>(null)
  const [movendo, setMovendo] = useState(false)

  // Ações semânticas em andamento.
  const [cardReuniao, setCardReuniao] = useState<CardPipeline | null>(null)
  const [cardPerda, setCardPerda] = useState<CardPipeline | null>(null)
  const [erroPerda, setErroPerda] = useState<ErroMotivoPerda | null>(null)
  const [cancelando, setCancelando] = useState(false)
  const [cardAderencia, setCardAderencia] = useState<CardPipeline | null>(null)
  const [perdidoAberto, setPerdidoAberto] = useState(false)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      // GET /v1/oportunidades — o backend já escopa por tenant + consultor (TenantScope);
      // o payload traz etapaComercial, scoreComercial e scoreAderencia derivados por lá.
      // GET /v1/dashboard/resumo — mesma lista escopada, já agregada: é de onde saem os
      // KPIs (nenhum número é calculado aqui). Se o resumo falhar, o quadro continua e
      // os KPIs mostram "—" — nunca uma soma feita na tela no lugar.
      const [page, resumoDashboard] = await Promise.all([
        oportunidadeService.listar(0, 200),
        dashboardService.getResumo().catch(() => null),
      ])
      setOportunidades(page.content ?? [])
      setResumo(resumoDashboard)
    } catch (e) {
      setErro(mensagemErroDe(e, MSG_MOVER))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (ehBackoffice) return
    void carregar()
  }, [carregar, ehBackoffice])

  const cards = useMemo<CardPipeline[]>(
    () =>
      oportunidades.map((o) => ({
        id: o.id,
        numero: o.numero,
        clienteId: o.clienteId,
        empresa: o.razaoSocial ?? o.nomeCliente ?? formatNumeroReserva(o.numero),
        cnpj: o.cnpj,
        produto: o.tipoNecessidade,
        valor: Number(o.valorEstimado ?? 0),
        proximaAcao: o.proximaAcao,
        aderencia: o.scoreAderencia,
        classe: o.scoreComercial?.classe ?? null,
        ultimaMovimentacaoEm: o.ultimaMovimentacaoEm,
        versao: o.versao,
        // Reserva anterior à derivação (backend antigo) cai no início do funil.
        etapa: o.etapaComercial ?? 'RESERVA',
        status: o.status ?? 'RASCUNHO',
      })),
    [oportunidades],
  )

  const porEtapa = useMemo(() => {
    const m = new Map<EtapaComercial, CardPipeline[]>()
    ;[...ETAPAS_VISIVEIS, 'PERDIDO' as EtapaComercial].forEach((e) => m.set(e, []))
    cards.forEach((c) => m.get(c.etapa)?.push(c))
    return m
  }, [cards])

  /**
   * KPIs — vêm prontos do `/v1/dashboard/resumo` (§7 do plano, 28/09), que agrega a
   * MESMA lista escopada do quadro: `pipelineTotal`/`ativas` (regra única de "ativa"),
   * volume e contagem por etapa, e por etapa a classe do Score Comercial com a contagem
   * por classe e a aderência média. A tela só lê; a única operação aqui é somar as
   * contagens por classe entre as etapas, que é exatamente para isso que `porClasse`
   * traz as três chaves (o frontend não conhece a tabela de score).
   */
  const kpis = useMemo(() => {
    const p = resumo?.pipeline
    const porEtapaResumo = p?.resumoPorEtapaComercial
    const porClasse: Record<ClasseScore, number> = { BAIXA: 0, MEDIA: 0, ALTA: 0 }
    if (porEtapaResumo) {
      Object.values(porEtapaResumo).forEach((e) => {
        CLASSES_SCORE.forEach((c) => {
          porClasse[c] += Number(e.porClasse?.[c] ?? 0)
        })
      })
    }
    return {
      disponivel: p != null,
      pipelineTotal: p?.pipelineTotal,
      qtdAtivas: p?.ativas,
      valorNegociacao: p?.valorPorEtapaComercial?.NEGOCIACAO,
      qtdNegociacao: p?.porEtapaComercial?.NEGOCIACAO,
      valorGanho: p?.valorPorEtapaComercial?.GANHO,
      qtdGanho: p?.porEtapaComercial?.GANHO,
      porClasse: porEtapaResumo ? porClasse : null,
      aderenciaMediaDe: (etapa: EtapaComercial): number | null =>
        porEtapaResumo?.[etapa]?.aderenciaMedia ?? null,
    }
  }, [resumo])

  const valorOuTraco = (v?: number | null) => (v != null ? formatBRL(Number(v)) : '—')
  const qtdOuTraco = (q?: number | null) =>
    q != null ? `${q} reserva(s)` : 'Resumo indisponível'

  /** Reverte o movimento otimista de um card. */
  const reverter = (card: CardPipeline, anterior: { status: StatusReserva; etapa: EtapaComercial }) =>
    setOportunidades((prev) =>
      prev.map((o) =>
        o.id === card.id ? { ...o, status: anterior.status, etapaComercial: anterior.etapa } : o,
      ),
    )

  /**
   * 409 `CONFLITO_VERSAO` (PDF §2.4): alguém mexeu na reserva depois que a tela a leu.
   * Recarrega a lista (versão nova, etapa nova) e avisa — o usuário decide se repete.
   */
  const tratarConflito = async () => {
    await carregar()
    enqueueSnackbar(MSG_CONFLITO_VERSAO, { variant: 'warning' })
  }

  /** Transição real na máquina de 18 status (Reunião → Estruturação). */
  const transicionar = async (card: CardPipeline, novoStatus: StatusReserva) => {
    if (!usuario?.id) {
      enqueueSnackbar(MSG_SEM_USUARIO, { variant: 'error' })
      return
    }
    setMovendo(true)
    const anterior = { status: card.status, etapa: card.etapa }
    // Otimista: o card muda de coluna na hora e volta se o backend recusar.
    setOportunidades((prev) =>
      prev.map((o) =>
        o.id === card.id ? { ...o, status: novoStatus, etapaComercial: 'ESTRUTURACAO' } : o,
      ),
    )
    try {
      const atualizada = await oportunidadeService.transicionarStatus(card.id, {
        novoStatus,
        usuarioId: usuario.id,
        versaoEsperada: card.versao ?? null,
      })
      // A resposta traz a versão nova e o score derivado — o card fica coerente sem reload.
      // `ultimaMovimentacaoEm` não vem no PATCH: a movimentação é agora.
      if (atualizada?.id) {
        setOportunidades((prev) =>
          prev.map((o) => (o.id === card.id ? { ...o, ...comMovimentacaoAgora(atualizada) } : o)),
        )
      }
      enqueueSnackbar(`${card.empresa}: ${ETAPA_COMERCIAL_LABEL[anterior.etapa]} → Estruturação`, {
        variant: 'success',
      })
    } catch (e) {
      reverter(card, anterior)
      if (ehConflitoVersao(e)) {
        await tratarConflito()
      } else {
        enqueueSnackbar(mensagemErroDe(e, MSG_MOVER), { variant: 'error' })
      }
    } finally {
      setMovendo(false)
    }
  }

  /**
   * Qualquer → Perdido: cancelamento com motivo PADRONIZADO do catálogo (decisão 8.6) e
   * observação quando o motivo exige. A versão vai da lista ATUAL (não do card capturado
   * ao arrastar): depois de um 409 a lista recarrega e a próxima tentativa já leva a
   * versão nova, com o motivo que o usuário escolheu preservado no diálogo.
   */
  const confirmarPerda = async (dados: DadosPerda) => {
    const card = cardPerda
    if (!card) return
    if (!usuario?.id) {
      enqueueSnackbar(MSG_SEM_USUARIO, { variant: 'error' })
      return
    }
    setCancelando(true)
    setErroPerda(null)
    const versaoAtual = oportunidades.find((o) => o.id === card.id)?.versao ?? card.versao ?? null
    try {
      const atualizada = await oportunidadeService.transicionarStatus(card.id, {
        novoStatus: 'CANCELADA',
        observacao: dados.observacao,
        motivoPerdaCodigo: dados.motivoPerdaCodigo,
        usuarioId: usuario.id,
        versaoEsperada: versaoAtual,
      })
      setOportunidades((prev) =>
        prev.map((o) =>
          o.id === card.id
            ? {
                ...o,
                ...comMovimentacaoAgora(atualizada?.id ? atualizada : {}),
                status: 'CANCELADA',
                etapaComercial: 'PERDIDO',
              }
            : o,
        ),
      )
      enqueueSnackbar(`${card.empresa} marcada como perdida`, { variant: 'success' })
      setCardPerda(null)
    } catch (e) {
      const erroCampo = erroMotivoPerdaDe(e)
      if (erroCampo) {
        // O backend disse qual campo faltou — o diálogo aponta e o usuário corrige.
        setErroPerda(erroCampo)
      } else if (ehConflitoVersao(e)) {
        await tratarConflito()
      } else {
        enqueueSnackbar(mensagemErroDe(e, MSG_MOVER), { variant: 'error' })
      }
    } finally {
      setCancelando(false)
    }
  }

  const soltarEm = (destino: EtapaComercial) => {
    const card = arrastando
    setColunaAlvo(null)
    setArrastando(null)
    if (!card) return
    const mov = movimentoDe(card, destino)
    switch (mov.tipo) {
      case 'nada':
        return
      case 'bloqueado':
        // Bloqueio informado, nunca silencioso — e nenhum request sai daqui.
        enqueueSnackbar(mov.motivo, { variant: 'warning' })
        return
      case 'reuniao':
        setCardReuniao(card)
        return
      case 'transicao':
        void transicionar(card, mov.novoStatus)
        return
      case 'perdido':
        setErroPerda(null)
        setCardPerda(card)
        return
    }
  }

  const renderCard = (card: CardPipeline, arrastavel: boolean) => {
    const movimentacao = tempoRelativo(card.ultimaMovimentacaoEm)
    return (
      <Paper
        key={card.id}
        data-testid={`card-${card.id}`}
        draggable={arrastavel && !movendo}
        onDragStart={() => setArrastando(card)}
        onDragEnd={() => {
          setArrastando(null)
          setColunaAlvo(null)
        }}
        // "Abrir →": o detalhe da reserva é a página existente de oportunidade.
        onClick={() => navigate(`/oportunidades/${card.id}`)}
        sx={{
          p: 1.25,
          mb: 1,
          borderRadius: 1.5,
          cursor: arrastavel ? 'grab' : 'default',
          opacity: arrastando?.id === card.id ? 0.5 : 1,
          '&:hover': { boxShadow: 2 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontSize="0.7rem" color="text.secondary" fontWeight={600}>
            {formatNumeroReserva(card.numero)}
          </Typography>
          {/* Classe do Score Comercial (PDF §2.3) — vem do backend, derivada da etapa. */}
          <ClasseScoreChip classe={card.classe} />
        </Box>
        <Typography fontSize="0.8rem" fontWeight={600} noWrap>
          {card.empresa}
        </Typography>
        <Typography fontSize="0.85rem" fontWeight={700} color="success.main">
          {formatBRL(card.valor)}
        </Typography>
        {formatCNPJ(card.cnpj) && (
          <Typography fontSize="0.68rem" color="text.secondary">
            {formatCNPJ(card.cnpj)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {card.produto && (
            <Chip
              label={card.produto}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'rgba(255, 98, 12, 0.08)',
                color: 'primary.main',
              }}
            />
          )}
          {/* Aderência calculada (Q5: continua ao lado do Score Comercial, com este rótulo) —
              clique abre o drawer com a decomposição. */}
          <Tooltip title="Ver a decomposição da aderência">
            <Chip
              label={`Aderência ${card.aderencia ?? '—'}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setCardAderencia(card)
              }}
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <PersonOutlineIcon sx={{ fontSize: 13 }} />
          <Typography fontSize="0.68rem" noWrap>
            {RESPONSAVEL_PROPRIO}
          </Typography>
        </Box>
        {movimentacao && (
          <Tooltip title={`Última movimentação: ${new Date(card.ultimaMovimentacaoEm!).toLocaleString('pt-BR')}`}>
            <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <UpdateIcon sx={{ fontSize: 13 }} />
              <Typography fontSize="0.68rem" data-testid="ultima-movimentacao">
                {movimentacao}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {card.proximaAcao && (
          <Tooltip title={card.proximaAcao}>
            <Box
              sx={{
                mt: 0.75,
                pt: 0.75,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
              }}
            >
              <FlagOutlinedIcon sx={{ fontSize: 13, mt: '1px', color: 'primary.main' }} />
              <Typography
                fontSize="0.68rem"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {card.proximaAcao}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Paper>
    )
  }

  if (ehBackoffice) {
    return (
      <Box>
        <PageHeader title="Pipeline Comercial" />
        <EstadoDeLista
          estado="semPermissao"
          mensagem="Esta visão é do consultor"
          detalhe="O back-office acompanha o pipeline pela jornada completa de 18 status."
        />
      </Box>
    )
  }

  const perdidos = porEtapa.get('PERDIDO') ?? []
  const bloqueioPara = (destino: EtapaComercial): string | null => {
    if (!arrastando) return null
    const mov = movimentoDe(arrastando, destino)
    return mov.tipo === 'bloqueado' ? mov.motivo : null
  }

  const legendaClasse = kpis.porClasse
    ? `Média ${kpis.porClasse.MEDIA} · Baixa ${kpis.porClasse.BAIXA}`
    : 'Resumo indisponível'

  return (
    <Box>
      <PageHeader
        title="Pipeline Comercial"
        subtitle="Arraste um card para agir: a etapa é derivada das ações — nunca gravada."
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          label="Pipeline total"
          valor={valorOuTraco(kpis.pipelineTotal)}
          legenda={kpis.qtdAtivas != null ? `${kpis.qtdAtivas} reserva(s) ativa(s)` : 'Resumo indisponível'}
          variante="escura"
          carregando={loading}
        />
        <KpiCard
          label="Negociação"
          valor={valorOuTraco(kpis.valorNegociacao)}
          legenda={qtdOuTraco(kpis.qtdNegociacao)}
          carregando={loading}
        />
        <KpiCard
          label="Ganho"
          valor={valorOuTraco(kpis.valorGanho)}
          legenda={qtdOuTraco(kpis.qtdGanho)}
          carregando={loading}
        />
        <KpiCard
          label={`Classe ${CLASSE_SCORE_LABEL.ALTA}`}
          valor={kpis.porClasse ? `${kpis.porClasse.ALTA} reserva(s)` : '—'}
          legenda={legendaClasse}
          carregando={loading}
        />
      </Box>

      {erro ? (
        <EstadoDeLista
          estado="erro"
          detalhe={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : loading ? (
        <EstadoDeLista estado="carregando" />
      ) : cards.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma reserva no pipeline"
          detalhe="Crie uma reserva em Minhas Reservas para começar."
          acao={{ label: 'Minhas Reservas', onClick: () => navigate('/reservas') }}
        />
      ) : (
        <QuadroRolavel aria-label="Etapas do pipeline">
          {ETAPAS_VISIVEIS.map((etapa) => {
            const lista = porEtapa.get(etapa) ?? []
            const motivoBloqueio = bloqueioPara(etapa)
            const alvoValido =
              arrastando != null && arrastando.etapa !== etapa && motivoBloqueio == null
            const aderenciaMedia = kpis.aderenciaMediaDe(etapa)

            return (
              <Box
                key={etapa}
                data-testid={`coluna-etapa-${etapa}`}
                sx={{ minWidth: 230, flex: '0 0 auto' }}
                onDragOver={(e) => {
                  // preventDefault é o que autoriza o drop no HTML5 DnD — só em alvo válido.
                  if (!alvoValido) return
                  e.preventDefault()
                  setColunaAlvo(etapa)
                }}
                onDragLeave={() => setColunaAlvo((c) => (c === etapa ? null : c))}
                onDrop={(e) => {
                  e.preventDefault()
                  soltarEm(etapa)
                }}
              >
                <Tooltip title={motivoBloqueio ?? ''}>
                  <Box
                    sx={{
                      bgcolor: ETAPA_COMERCIAL_COR[etapa],
                      color: '#fff',
                      px: 1.5,
                      py: 1,
                      borderRadius: '10px 10px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      opacity: motivoBloqueio ? 0.45 : 1,
                    }}
                  >
                    {ETAPA_COMERCIAL_LABEL[etapa]}
                    <Chip
                      label={lista.length}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: '#fff', height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Tooltip>

                <Box
                  sx={{
                    bgcolor: colunaAlvo === etapa ? 'rgba(46, 204, 113, 0.12)' : '#F4F6F9',
                    outline: colunaAlvo === etapa ? '2px dashed #2ECC71' : 'none',
                    borderRadius: '0 0 10px 10px',
                    p: 1,
                    minHeight: 220,
                    transition: 'background-color 120ms',
                  }}
                >
                  {/* Aderência média da etapa — agregada pelo backend no resumo (Q5). */}
                  {aderenciaMedia != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid={`aderencia-media-${etapa}`}
                      sx={{ display: 'block', textAlign: 'center', pb: 0.5 }}
                    >
                      Aderência média {aderenciaMedia}
                    </Typography>
                  )}
                  {motivoBloqueio && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', pb: 0.5 }}
                    >
                      {motivoBloqueio}
                    </Typography>
                  )}
                  {lista.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', py: 3 }}
                    >
                      —
                    </Typography>
                  ) : (
                    lista.map((card) => renderCard(card, true))
                  )}
                </Box>
              </Box>
            )
          })}

          {/* Perdido recolhido (o Figma omite a coluna): contador + painel expansível.
              Continua sendo alvo de drop — soltar aqui abre o diálogo de motivo. */}
          <Box
            data-testid="coluna-etapa-PERDIDO"
            sx={{ minWidth: perdidoAberto ? 230 : 120, flex: '0 0 auto' }}
            onDragOver={(e) => {
              if (!arrastando || bloqueioPara('PERDIDO')) return
              e.preventDefault()
              setColunaAlvo('PERDIDO')
            }}
            onDragLeave={() => setColunaAlvo((c) => (c === 'PERDIDO' ? null : c))}
            onDrop={(e) => {
              e.preventDefault()
              soltarEm('PERDIDO')
            }}
          >
            <Box
              sx={{
                bgcolor: ETAPA_COMERCIAL_COR.PERDIDO,
                color: '#fff',
                px: 1.5,
                py: 1,
                borderRadius: perdidoAberto ? '10px 10px 0 0' : '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                fontWeight: 600,
                outline: colunaAlvo === 'PERDIDO' ? '2px dashed #E74C3C' : 'none',
              }}
            >
              {ETAPA_COMERCIAL_LABEL.PERDIDO}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={perdidos.length}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: '#fff', height: 20, fontSize: '0.7rem' }}
                />
                <IconButton
                  size="small"
                  aria-label={perdidoAberto ? 'Ocultar perdidas' : 'Mostrar perdidas'}
                  onClick={() => setPerdidoAberto((v) => !v)}
                  sx={{ color: '#fff', p: 0.25 }}
                >
                  {perdidoAberto ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>
            {perdidoAberto && (
              <Box sx={{ bgcolor: '#F4F6F9', borderRadius: '0 0 10px 10px', p: 1, minHeight: 120 }}>
                {perdidos.length === 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', py: 3 }}
                  >
                    —
                  </Typography>
                ) : (
                  perdidos.map((card) => renderCard(card, false))
                )}
              </Box>
            )}
          </Box>
        </QuadroRolavel>
      )}

      {/* Reserva → Reunião: a ação é AGENDAR, não transicionar. O diálogo existente cria a
          reunião vinculada; a etapa muda por derivação quando a reunião de primeiro
          contato for realizada (regra do backend, 19/08). */}
      <ReuniaoDialog
        open={!!cardReuniao}
        onClose={() => setCardReuniao(null)}
        prefill={
          cardReuniao
            ? {
                oportunidadeId: cardReuniao.id,
                clienteId: cardReuniao.clienteId,
                clienteNome: cardReuniao.empresa,
                titulo: `Reunião — ${cardReuniao.empresa}`,
                // Só a reunião de PRIMEIRO CONTATO realizada move a etapa (ReuniaoService);
                // vem pré-marcada para o consultor não agendar uma que nunca avança o card.
                primeiroContato: true,
              }
            : undefined
        }
        onSaved={() => {
          enqueueSnackbar(
            'Reunião vinculada à reserva. A etapa avança quando a reunião de primeiro contato for realizada.',
            { variant: 'info' },
          )
          void carregar()
        }}
      />

      <MotivoPerdaDialog
        open={!!cardPerda}
        empresa={cardPerda?.empresa}
        destino="CANCELADA"
        submitting={cancelando}
        erro={erroPerda}
        onClose={() => {
          setCardPerda(null)
          setErroPerda(null)
        }}
        onConfirm={(dados) => void confirmarPerda(dados)}
      />

      <AderenciaDrawer
        oportunidadeId={cardAderencia?.id ?? null}
        empresa={cardAderencia?.empresa}
        onClose={() => setCardAderencia(null)}
      />
    </Box>
  )
}

export default PipelineConsultorPage
